import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const STATUS_ICONS = {
  present: { label: 'P', color: 'bg-green-500 text-white' },
  absent: { label: 'A', color: 'bg-red-500 text-white' },
  excused: { label: 'E', color: 'bg-yellow-500 text-white' },
  unmarked: { label: '—', color: 'bg-gray-100 text-gray-400' }
};

const AttendancePage = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});  // { studentId: status }
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load courses
  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get('/courses?status=active&type=long_course');
        setCourses(data.courses);
        if (data.courses.length > 0) setSelectedCourse(data.courses[0]._id);

        const { data: sumData } = await api.get('/attendance/summary');
        setSummary(sumData.summary);
      } catch (err) {
        console.error('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // Load attendance for selected course + date
  useEffect(() => {
    if (!selectedCourse || !selectedDate) return;
    const fetch = async () => {
      try {
        const { data } = await api.get(`/attendance/course/${selectedCourse}?date=${selectedDate}`);
        setStudents(data.students);

        // Build records map from existing attendance
        const map = {};
        data.students.forEach(s => { map[s._id] = 'unmarked'; });
        data.records.forEach(r => { map[r.student._id || r.student] = r.status; });
        setRecords(map);
        setSaved(false);
      } catch (err) {
        console.error('Failed to load attendance');
      }
    };
    fetch();
  }, [selectedCourse, selectedDate]);

  const cycleStatus = (studentId) => {
    const order = ['unmarked', 'present', 'absent', 'excused'];
    const current = records[studentId] || 'unmarked';
    const next = order[(order.indexOf(current) + 1) % order.length];
    setRecords(prev => ({ ...prev, [studentId]: next }));
    setSaved(false);
  };

  const markAllPresent = () => {
    const updated = {};
    students.forEach(s => { updated[s._id] = 'present'; });
    setRecords(updated);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const recs = Object.entries(records)
        .filter(([_, status]) => status !== 'unmarked')
        .map(([studentId, status]) => ({ studentId, status }));

      await api.post('/attendance/mark', {
        courseId: selectedCourse,
        date: selectedDate,
        records: recs
      });
      setSaved(true);

      // Refresh summary
      const { data } = await api.get('/attendance/summary');
      setSummary(data.summary);
    } catch (err) {
      console.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const markedCount = Object.values(records).filter(s => s === 'present').length;
  const totalStudents = students.length;

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div>

      {/* Summary cards — hidden on mobile */}
      {summary.length > 0 && (
        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {summary.map(s => (
            <div key={s.courseId} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="text-sm font-medium text-gray-700 truncate">{s.courseName}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-bold text-gray-900">
                  {s.attendanceRate !== null ? `${s.attendanceRate}%` : '—'}
                </span>
                <span className="text-xs text-gray-500">{s.enrolledCount} students</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {courses.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No active courses. Create and publish a course to start tracking attendance.
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm mb-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Course</label>
                <select
                  value={selectedCourse}
                  onChange={e => setSelectedCourse(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                >
                  {courses.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={markAllPresent}
                  className="text-sm text-brand-500 hover:text-brand-600 font-medium whitespace-nowrap pb-2"
                >
                  Mark All Present
                </button>
              </div>
            </div>
          </div>

          {/* Roll call */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {students.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No enrolled students in this course yet.
              </div>
            ) : (
              <>
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {markedCount}/{totalStudents} present
                  </span>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-green-500 inline-block" /> Present
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-red-500 inline-block" /> Absent
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-yellow-500 inline-block" /> Excused
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-gray-50">
                  {students.map(student => {
                    const status = records[student._id] || 'unmarked';
                    const icon = STATUS_ICONS[status];
                    return (
                      <div
                        key={student._id}
                        className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => cycleStatus(student._id)}
                      >
                        <button
                          className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${icon.color}`}
                        >
                          {icon.label}
                        </button>
                        <div className="ml-4 flex-1">
                          <div className="text-sm font-medium text-gray-900">{student.name}</div>
                          <div className="text-xs text-gray-500">{student.email}</div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {student.totalAttended || 0} classes attended
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                  {saved ? (
                    <span className="text-sm text-green-600 font-medium">Saved</span>
                  ) : (
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                    >
                      {saving ? 'Saving...' : 'Save Attendance'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AttendancePage;
